import React from 'react';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import { BIDDING_STRATEGIES, type BiddingStrategy } from '@/types/adGroup';
import { formatBiddingStrategy } from '@/utils/formatEnum';

interface AdGroupBiddingStepProps {
  biddingStrategy: BiddingStrategy;
  campaignBiddingStrategy?: string; // The campaign's bidding strategy for display
  cpcBidDollars?: string;
  targetCpaDollars?: string;
  targetRoas?: string;
  onChangeBiddingStrategy: (strategy: BiddingStrategy) => void;
  onChangeCpcBid: (value: string) => void;
  onChangeTargetCpa: (value: string) => void;
  onChangeTargetRoas: (value: string) => void;
  errors?: {
    biddingStrategy?: string;
    cpcBid?: string;
    targetCpa?: string;
    targetRoas?: string;
  };
}

export const AdGroupBiddingStep: React.FC<AdGroupBiddingStepProps> = ({
  biddingStrategy,
  campaignBiddingStrategy,
  cpcBidDollars,
  targetCpaDollars,
  targetRoas,
  onChangeBiddingStrategy,
  onChangeCpcBid,
  onChangeTargetCpa,
  onChangeTargetRoas,
  errors,
}) => {
  const selectedStrategy = BIDDING_STRATEGIES.find((s) => s.value === biddingStrategy);

  // Format the campaign's bidding strategy for display
  const formattedCampaignStrategy = campaignBiddingStrategy
    ? formatBiddingStrategy(campaignBiddingStrategy)
    : null;

  // Filter out strategies that match the campaign's strategy (to avoid redundancy)
  // Keep INHERIT and strategies that don't match the campaign's strategy
  const filteredStrategies = BIDDING_STRATEGIES.filter((strategy) => {
    if (strategy.value === 'INHERIT') return true;
    // Exclude if this strategy matches the campaign's bidding strategy
    if (campaignBiddingStrategy && strategy.value === campaignBiddingStrategy) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Bidding Strategy</h3>
        <p className="text-sm text-gray-600">
          Choose how you want to bid for ad placements in this ad group.
        </p>
      </div>

      {/* Bidding Strategy Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Bidding Strategy <span className="text-red-500">*</span>
        </label>
        <div className="grid gap-3">
          {filteredStrategies.map((strategy) => {
            const isSelected = biddingStrategy === strategy.value;
            // Build description - for INHERIT, show the campaign's formatted strategy
            const description = strategy.value === 'INHERIT' && formattedCampaignStrategy
              ? <>Use the <strong>{formattedCampaignStrategy}</strong> bidding strategy set at the campaign level (recommended)</>
              : strategy.description;
            return (
              <label
                key={strategy.value}
                className={cn(
                  'relative flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all',
                  'hover:bg-gray-50 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2',
                  {
                    'border-blue-600 bg-blue-50': isSelected,
                    'border-gray-200': !isSelected,
                  }
                )}
              >
                <input
                  type="radio"
                  name="bidding-strategy"
                  value={strategy.value}
                  checked={isSelected}
                  onChange={() => onChangeBiddingStrategy(strategy.value)}
                  className="sr-only"
                  aria-describedby={`strategy-${strategy.value}-description`}
                />
                <div className="flex items-start">
                  <div
                    className={cn(
                      'flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 mt-0.5',
                      {
                        'border-blue-600 bg-blue-600': isSelected,
                        'border-gray-300': !isSelected,
                      }
                    )}
                  >
                    {isSelected && (
                      <div className="w-2 h-2 rounded-full bg-white" aria-hidden="true" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-gray-900">
                      {strategy.label}
                    </span>
                    <p
                      id={`strategy-${strategy.value}-description`}
                      className="text-xs text-gray-500 mt-1"
                    >
                      {description}
                    </p>
                  </div>
                </div>
              </label>
            );
          })}
        </div>
        {errors?.biddingStrategy && (
          <p className="mt-2 text-sm text-red-600">{errors.biddingStrategy}</p>
        )}
      </div>

      {/* Conditional Bid Amount Input */}
      {selectedStrategy?.requiresInput && (
        <div className="pt-4 border-t border-gray-200">
          {selectedStrategy.inputType === 'cpc' && (
            <div>
              <label htmlFor="cpc-bid" className="block text-sm font-medium text-gray-700 mb-2">
                Maximum CPC Bid <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  id="cpc-bid"
                  type="number"
                  value={cpcBidDollars || ''}
                  onChange={(e) => onChangeCpcBid(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className={cn('pl-7', {
                    'border-red-500 focus:border-red-500 focus:ring-red-500': errors?.cpcBid,
                  })}
                  aria-invalid={!!errors?.cpcBid}
                  aria-describedby={errors?.cpcBid ? 'cpc-bid-error' : 'cpc-bid-help'}
                />
              </div>
              {errors?.cpcBid && (
                <p id="cpc-bid-error" className="mt-1 text-sm text-red-600">
                  {errors.cpcBid}
                </p>
              )}
              <p id="cpc-bid-help" className="mt-1 text-xs text-gray-500">
                The maximum amount you're willing to pay for a click on your ad.
              </p>
            </div>
          )}

          {selectedStrategy.inputType === 'cpa' && (
            <div>
              <label htmlFor="target-cpa" className="block text-sm font-medium text-gray-700 mb-2">
                Target CPA <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  id="target-cpa"
                  type="number"
                  value={targetCpaDollars || ''}
                  onChange={(e) => onChangeTargetCpa(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className={cn('pl-7', {
                    'border-red-500 focus:border-red-500 focus:ring-red-500': errors?.targetCpa,
                  })}
                  aria-invalid={!!errors?.targetCpa}
                  aria-describedby={errors?.targetCpa ? 'target-cpa-error' : 'target-cpa-help'}
                />
              </div>
              {errors?.targetCpa && (
                <p id="target-cpa-error" className="mt-1 text-sm text-red-600">
                  {errors.targetCpa}
                </p>
              )}
              <p id="target-cpa-help" className="mt-1 text-xs text-gray-500">
                The average amount you want to pay for each conversion.
              </p>
            </div>
          )}

          {selectedStrategy.inputType === 'roas' && (
            <div>
              <label htmlFor="target-roas" className="block text-sm font-medium text-gray-700 mb-2">
                Target ROAS <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Input
                  id="target-roas"
                  type="number"
                  value={targetRoas || ''}
                  onChange={(e) => onChangeTargetRoas(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className={cn({
                    'border-red-500 focus:border-red-500 focus:ring-red-500': errors?.targetRoas,
                  })}
                  aria-invalid={!!errors?.targetRoas}
                  aria-describedby={errors?.targetRoas ? 'target-roas-error' : 'target-roas-help'}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">×</span>
              </div>
              {errors?.targetRoas && (
                <p id="target-roas-error" className="mt-1 text-sm text-red-600">
                  {errors.targetRoas}
                </p>
              )}
              <p id="target-roas-help" className="mt-1 text-xs text-gray-500">
                The target return on ad spend. For example, 2.5 means you want $2.50 in revenue for
                every $1 spent on ads.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
